// 日期处理工具函数

/**
 * 格式化日期
 * @param {string|Date} date - 日期字符串或Date对象
 * @param {string} format - 格式字符串
 * @returns {string} 格式化后的日期字符串
 */
export function formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');

  const formatMap = {
    'YYYY': year,
    'MM': month,
    'DD': day,
    'HH': hours,
    'mm': minutes,
    'ss': seconds,
    'M': d.getMonth() + 1,
    'D': d.getDate(),
    'H': d.getHours(),
    'm': d.getMinutes(),
    's': d.getSeconds()
  };

  let formatted = format;
  Object.keys(formatMap).forEach(key => {
    formatted = formatted.replace(new RegExp(key, 'g'), formatMap[key]);
  });

  return formatted;
}

/**
 * 获取相对时间（如：2小时前、3天前）
 * @param {string|Date} date - 日期字符串或Date对象
 * @returns {string} 相对时间字符串
 */
export function getRelativeTime(date) {
  const now = new Date();
  const target = new Date(date);
  const diff = now - target;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const months = Math.floor(diff / 2592000000);
  const years = Math.floor(diff / 31536000000);

  if (minutes < 1) {
    return '刚刚';
  } else if (minutes < 60) {
    return `${minutes}分钟前`;
  } else if (hours < 24) {
    return `${hours}小时前`;
  } else if (days < 30) {
    return `${days}天前`;
  } else if (months < 12) {
    return `${months}个月前`;
  } else {
    return `${years}年前`;
  }
}

/**
 * 获取文章归档信息
 * @param {Array} posts - 文章数组
 * @returns {Object} 归档信息
 */
export function getArchiveInfo(posts) {
  const archiveMap = new Map();
  
  posts.forEach(post => {
    const date = new Date(post.createdAt);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const key = `${year}-${String(month).padStart(2, '0')}`;
    
    if (!archiveMap.has(key)) {
      archiveMap.set(key, {
        year,
        month,
        key,
        count: 0,
        posts: []
      });
    }
    
    const archive = archiveMap.get(key);
    archive.count++;
    archive.posts.push(post);
  });
  
  // 按日期排序（最新的在前）
  const archives = Array.from(archiveMap.values()).sort((a, b) => {
    if (a.year !== b.year) {
      return b.year - a.year;
    }
    return b.month - a.month;
  });
  
  return archives;
}

/**
 * 获取标签统计
 * @param {Array} posts - 文章数组
 * @returns {Array} 标签统计数组
 */
export function getTagStats(posts) {
  const tagMap = new Map();
  
  posts.forEach(post => {
    if (post.tags && Array.isArray(post.tags)) {
      post.tags.forEach(tag => {
        if (tagMap.has(tag)) {
          tagMap.set(tag, tagMap.get(tag) + 1);
        } else {
          tagMap.set(tag, 1);
        }
      });
    }
  });
  
  // 转换为数组并按数量排序
  const tagStats = Array.from(tagMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
  
  return tagStats;
}

/**
 * 获取文章阅读时长估算
 * @param {string} content - 文章内容
 * @returns {number} 预计阅读时间（分钟）
 */
export function getReadingTime(content) {
  const wordsPerMinute = 200; // 平均阅读速度
  const words = content.length; // 简单计算字符数
  const minutes = Math.ceil(words / wordsPerMinute);
  return Math.max(1, minutes);
}